// Notifications popover. Triggered from a bell button in the top
// bar. Renders the same list as the existing NotificationsPanel
// (a list of unread + recently-read items with "open / mark read"
// actions) but anchored to the trigger rather than sliding up
// from the bottom. On viewports < `lg` the shell should NOT use
// this — fall back to the existing BottomSheet-based panel.

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Check, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ModuleId } from "@/pages/AppShell";
import type { Notification } from "@/pages/panels/notifications";

const ICON_BY_KIND: Record<string, LucideIcon> = {
  chat: Bell,
  status: Check,
  mood: Bell,
  list: Check,
  system: Bell,
  tree: Bell,
};

const KIND_LABEL: Record<string, string> = {
  chat: "對話",
  status: "打卡",
  mood: "心情",
  list: "清單",
  system: "系統",
  tree: "族譜",
};

export function NotificationsPopover({
  items,
  unreadCount,
  onMarkAllRead,
  onMarkRead,
  onNavigate,
  trigger,
}: {
  items: Notification[];
  unreadCount: number;
  onMarkAllRead: () => void;
  onMarkRead: (id: string) => void;
  onNavigate: (id: ModuleId) => void;
  trigger: React.ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="w-[22rem] overflow-hidden rounded-2xl border border-border/60 p-0 shadow-2xl"
      >
        <header className="flex items-center gap-2 border-b border-border/60 bg-secondary/30 px-3 py-2.5">
          <h2 className="text-sm font-semibold">通知</h2>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {unreadCount}
            </span>
          )}
          <button
            type="button"
            onClick={onMarkAllRead}
            disabled={unreadCount === 0}
            className="ml-auto text-xs text-primary transition-colors hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            全部已讀
          </button>
        </header>
        <ul className="max-h-[60vh] divide-y divide-border/40 overflow-y-auto">
          {items.length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-muted-foreground">
              暫無新通知
            </li>
          ) : (
            items.map((n) => {
              const Icon = ICON_BY_KIND[n.kind] ?? Bell;
              return (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onMarkRead(n.id);
                      onNavigate(n.target);
                    }}
                    className={cn(
                      "flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-secondary",
                      n.unread && "bg-primary/[0.04]",
                    )}
                  >
                    {n.who ? (
                      <span
                        aria-hidden
                        className={cn(
                          "grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br text-xs font-semibold text-white",
                          n.who.color,
                        )}
                      >
                        {n.who.initials}
                      </span>
                    ) : (
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary text-foreground/70">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                        <span className="font-medium text-foreground/85">
                          {KIND_LABEL[n.kind] ?? n.kind}
                        </span>
                        {n.who && (
                          <>
                            <span aria-hidden>·</span>
                            <span className="truncate">{n.who.name}</span>
                          </>
                        )}
                        {n.unread && (
                          <span
                            aria-label="未讀"
                            className="ml-auto inline-block h-2 w-2 shrink-0 rounded-full bg-primary"
                          />
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-sm font-medium">{n.title}</div>
                      <div className="truncate text-xs text-muted-foreground">{n.body}</div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground/80">{n.time}</div>
                    </div>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
